import {
  ElementStatus,
  PageStructure,
  SelectedElementContainer,
  ToolMessage,
  useLinearTransform,
  ViewElementContainer
} from '@st/document-viewer-kit'
import { move, toDragPointerEvent, useUpdateBounds } from '@st/document-viewer-kit'
import { StampAnnotation } from '@st/sdk'
import { Offset } from '@st/geom'
import { useKeyboardShortcuts } from '@st/react-util/shortcuts'
import { FontSizeLargerIcon, FontSizeSmallerIcon } from '@st/theme'
import { useMemo, useState } from 'react'
import { DeleteIcon } from '~/components/icons'
import { AnnotationFloatingToolbar, FloatingToolbarButton } from '../../floating-toolbar'
import { StampTemplate, StampToolState } from '~/st-doc-viewer/annotations'
import { DocumentAnnotationDisplay } from '~/st-doc-viewer/document-annotation-display'
import { recomputeStampLayoutAndStyle } from '../stamps-core/stamp-layout'
import { VisualSelect, VisualSelectSection } from '../stamps-core/visual-select'
import { syncToolStateWithAnnotation } from './tool-state'
import { TICK_MARK_STAMP_TEMPLATE_SECTIONS } from './templates'

export function StampToolEditor({
  toolState,
  activeAnnotation,
  send
}: {
  toolState: StampToolState
  activeAnnotation: StampAnnotation | undefined
  send: (message: ToolMessage<StampAnnotation, StampToolState>) => void
}) {
  const [isOpen, setIsOpen] = useState(false)

  const sections = TICK_MARK_STAMP_TEMPLATE_SECTIONS

  const placeholderTemplate = useMemo(() => {
    if (!toolState.settings.templateId) return undefined
    return sections.flatMap((s) => s.templates).find((t) => t.id === toolState.settings.templateId)
  }, [toolState.settings.templateId])

  return (
    <VisualSelect
      className="w-64"
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      placeholder={
        placeholderTemplate ? (
          <div className="h-8">
            <StampOptionPreview template={placeholderTemplate} />
          </div>
        ) : (
          <div className="flex h-8 items-center px-2 text-base font-normal text-gray-700">
            {'Select a stamp'}
          </div>
        )
      }
    >
      {sections.map((section) => (
        <VisualSelectSection key={section.title} title={section.title} layout={section.layout}>
          {section.templates.map((template, index) => (
            <div
              key={index}
              className="h-10 cursor-pointer hover:bg-gray-100"
              onClick={() => {
                setIsOpen(false)
                send({
                  type: 'updateTool',
                  tool: {
                    name: 'stamp',
                    settings: { templateId: template.id, stampSize: toolState.settings.stampSize }
                  }
                })
              }}
            >
              <StampOptionPreview template={template} showDescription={section.layout === 'rows'} />
            </div>
          ))}
        </VisualSelectSection>
      ))}
    </VisualSelect>
  )
}

function selEffectiveAnnotation(settings: StampToolState['settings']) {
  if (!settings.templateId) return undefined
  const template = TICK_MARK_STAMP_TEMPLATE_SECTIONS.flatMap((s) => s.templates).find(
    (t) => t.id === settings.templateId
  )
  if (!template) return undefined
  return recomputeStampLayoutAndStyle({
    ...template.annotation,
    stampSize: settings.stampSize
  })
}

export function StampToolOverlay({
  page,
  toolState,
  activeAnnotation,
  status,
  send
}: {
  page: PageStructure
  toolState: StampToolState
  activeAnnotation: StampAnnotation | undefined
  status: ElementStatus
  send: (message: ToolMessage<StampAnnotation, StampToolState>) => void
}) {
  const transform = useLinearTransform()
  const [pointer, setPointer] = useState<{ x: number; y: number } | undefined>(undefined)

  const ghostAnnotation = useMemo(
    () => selEffectiveAnnotation(toolState.settings),
    [toolState.settings]
  )

  return (
    <>
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          backgroundColor: 'transparent'
        }}
        onPointerMove={(e) => {
          if (activeAnnotation || !toolState.settings.templateId) {
            setPointer(undefined)
            return
          }
          const pointerEvent = toDragPointerEvent(e, transform, 'drawing-canvas')
          setPointer(pointerEvent.point)
        }}
        onPointerLeave={() => {
          setPointer(undefined)
        }}
        onPointerDown={(e) => {
          e.preventDefault()
          e.stopPropagation()

          const pointerEvent = toDragPointerEvent(e, transform, 'drawing-canvas')

          if (activeAnnotation) {
            send({ type: 'deselectAnnotation' })
            return
          } else if (toolState.settings.templateId) {
            const effective = selEffectiveAnnotation(toolState.settings)
            if (!effective) return
            const { id: _templateId, time: _templateTime, ...template } = effective

            const baseAnnotation = recomputeStampLayoutAndStyle({
              ...template,
              type: 'stamp',
              pageNumber: page.pageNumber,
              bounds: {
                x: pointerEvent.point.x,
                y: pointerEvent.point.y,
                width: 0,
                height: 0
              }
            })
            const annotation = {
              ...baseAnnotation,
              // we want to center this
              bounds: {
                width: baseAnnotation.bounds.width,
                height: baseAnnotation.bounds.height,
                x: baseAnnotation.bounds.x - baseAnnotation.bounds.width / 2,
                y: baseAnnotation.bounds.y - baseAnnotation.bounds.height / 2
              }
            }

            send({
              type: 'requestAnnotationCreation',
              annotationDraft: annotation
            })
          }
        }}
      />
      {ghostAnnotation && pointer && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            pointerEvents: 'none',
            transform: `translate(${pointer.x}px, ${pointer.y}px)`
          }}
        >
          <div style={{ transform: 'translate(-50%, -50%)' }}>
            <DocumentAnnotationDisplay annotation={ghostAnnotation} />
          </div>
        </div>
      )}
    </>
  )
}

export function StampAnnotationElement({
  annotation,
  page,
  status,
  send
}: {
  annotation: StampAnnotation
  page: PageStructure
  status: ElementStatus
  send: (message: ToolMessage<StampAnnotation, StampToolState>) => void
}) {
  switch (status) {
    case 'selected':
    case 'editing':
      return <StampAnnotationSelected annotation={annotation} send={send} />
    case 'none':
      return (
        <TextStampAnnotationView
          annotation={annotation}
          onSelect={() => send({ type: 'selectAnnotation', annotationId: annotation.id })}
        />
      )
  }
}

function TextStampAnnotationView({
  annotation,
  onSelect
}: {
  annotation: StampAnnotation
  onSelect: () => void
}) {
  return (
    <ViewElementContainer key={annotation.id} bounds={annotation.bounds} onClick={onSelect}>
      <DocumentAnnotationDisplay annotation={annotation} />
    </ViewElementContainer>
  )
}

function StampAnnotationSelected({
  annotation,
  send
}: {
  annotation: StampAnnotation
  send: (message: ToolMessage<StampAnnotation, StampToolState>) => void
}) {
  const { bounds, onMove } = useUpdateBounds({
    initialBounds: annotation.bounds,
    onCommit: (event) => {
      send({
        type: 'updateAnnotationDraft',
        annotationDraft: { ...annotation, bounds: event.newBounds }
      })
    }
  })

  function handleNudge(offset: Partial<Offset>) {
    send({
      type: 'updateAnnotationDraft',
      annotationDraft: { ...annotation, bounds: move(annotation.bounds, offset) }
    })
  }

  useKeyboardShortcuts({
    Backspace: () => {
      send({ type: 'deleteAnnotation', annotationId: annotation.id })
    },
    ArrowUp: () => handleNudge({ dy: -5 }),
    ArrowDown: () => handleNudge({ dy: 5 }),
    ArrowLeft: () => handleNudge({ dx: -5 }),
    ArrowRight: () => handleNudge({ dx: 5 })
  })

  return (
    <SelectedElementContainer
      bounds={bounds}
      onMove={onMove}
      resizable={false}
      onDoubleClick={() => {
        send({ type: 'beginEditingAnnotation', annotationDraft: annotation })
      }}
      toolbar={
        <AnnotationFloatingToolbar>
          <FloatingToolbarButton
            icon={<FontSizeSmallerIcon className="size=6" />}
            onClick={() => {
              const newAnnotation: StampAnnotation = recomputeStampLayoutAndStyle({
                ...annotation,
                stampSize: annotation.stampSize - 2
              })

              send({
                type: 'updateAnnotationDraft',
                annotationDraft: newAnnotation
              })
              send({ type: 'updateTool', tool: syncToolStateWithAnnotation(newAnnotation) })
            }}
          />
          <FloatingToolbarButton
            icon={<FontSizeLargerIcon className="size-6" />}
            onClick={() => {
              const newAnnotation: StampAnnotation = recomputeStampLayoutAndStyle({
                ...annotation,
                stampSize: annotation.stampSize + 2
              })

              send({
                type: 'updateAnnotationDraft',
                annotationDraft: newAnnotation
              })
              send({ type: 'updateTool', tool: syncToolStateWithAnnotation(newAnnotation) })
            }}
          />
          <FloatingToolbarButton
            icon={<DeleteIcon className="size-6" />}
            onClick={() => {
              send({ type: 'deleteAnnotation', annotationId: annotation.id })
            }}
          />
        </AnnotationFloatingToolbar>
      }
    >
      <DocumentAnnotationDisplay annotation={annotation} />
    </SelectedElementContainer>
  )
}

function StampOptionPreview({
  template,
  showDescription = true
}: {
  template: StampTemplate
  showDescription?: boolean
}) {
  return (
    <div className="flex h-full w-full items-center gap-2">
      <div className="flex size-10 items-center justify-center">
        <DocumentAnnotationDisplay annotation={template.annotation} />
      </div>
      {showDescription && (
        <span className="flex items-center text-base font-normal text-gray-700">
          {template.label}
        </span>
      )}
    </div>
  )
}
